import { useState } from "react";
import { reviews } from "@/mocks/products";
import ReviewWriteModal from "@/components/base/ReviewWriteModal";

const STAR_LABELS = ["", "별로예요", "그저 그래요", "괜찮아요", "좋아요", "최고예요"];

const ReviewCard = ({ review }: { review: typeof reviews[0] }) => (
  <div className="bg-stone-50 rounded-2xl p-5 flex flex-col gap-3">
    {/* Stars + Product */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <div key={s} className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={`text-xs ${s <= review.rating ? "ri-star-fill text-amber-400" : "ri-star-line text-stone-300"}`} />
          </div>
        ))}
        <span className="text-xs font-bold text-stone-700 ml-1">{STAR_LABELS[review.rating]}</span>
      </div>
      <span className="text-[10px] text-stone-400">{review.date}</span>
    </div>

    {/* Comment */}
    <p className="text-sm text-stone-600 leading-relaxed flex-1">
      &ldquo;{review.comment}&rdquo;
    </p>

    {/* Product Tag */}
    <div className="inline-flex items-center gap-1.5 bg-white border border-stone-200 rounded-full px-2.5 py-1 w-fit">
      <i className="ri-lock-line text-stone-400 text-[10px]" />
      <span className="text-[10px] font-semibold text-stone-500">{review.product}</span>
    </div>

    {/* User */}
    <div className="flex items-center gap-2.5 pt-3 border-t border-stone-200">
      <img
        src={review.avatar}
        alt={review.name}
        className="w-8 h-8 rounded-full object-cover object-top flex-shrink-0"
      />
      <div>
        <p className="text-xs font-bold text-stone-800">{review.name}</p>
        <p className="text-[10px] text-stone-400">{review.location} · 설치 완료</p>
      </div>
    </div>
  </div>
);

const ReviewSection = () => {
  const [showWriteModal, setShowWriteModal] = useState(false);
  const [visibleCount, setVisibleCount] = useState(4);

  const visibleReviews = reviews.slice(0, visibleCount);
  const hasMore = visibleCount < reviews.length;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">

          {/* Left: Image + Info */}
          <div className="lg:sticky lg:top-28">
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden w-full" style={{ aspectRatio: "4/5" }}>
              <img
                src="https://public.readdy.ai/ai/img_res/e6d1d9b9cc691c4cf02a131981fe0374.jpg"
                alt="도어준 전문 설치 서비스"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

              {/* Overlay text */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-xs font-bold tracking-widest uppercase text-white/60 mb-1">Customer Reviews</p>
                <h2 className="text-2xl md:text-3xl font-black text-white leading-tight mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  설치 고객 후기
                </h2>
                <p className="text-sm text-white/70">실제 도어준을 설치한 고객들의 생생한 후기</p>
              </div>

              {/* Floating Rating Badge */}
              <div className="absolute top-4 right-4 bg-white rounded-xl px-3 py-2.5 text-center">
                <div className="flex items-center gap-1 justify-center mb-0.5">
                  {[1,2,3,4,5].map((s) => (
                    <div key={s} className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-star-fill text-amber-400 text-[10px]" />
                    </div>
                  ))}
                </div>
                <p className="text-lg font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>4.9</p>
                <p className="text-[10px] text-stone-400">2,400+ 리뷰</p>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-3 mt-4">
              {[
                { icon: "ri-home-4-line", value: "15,000+", label: "설치 완료" },
                { icon: "ri-shield-check-line", value: "99.8%", label: "보안 신뢰도" },
                { icon: "ri-customer-service-2-line", value: "24시간", label: "고객 지원" },
              ].map((s) => (
                <div key={s.label} className="bg-stone-50 rounded-xl p-3.5 text-center">
                  <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg mx-auto mb-2">
                    <i className={`${s.icon} text-stone-600 text-sm`} />
                  </div>
                  <p className="text-base font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                  <p className="text-[10px] text-stone-500 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Write Review CTA */}
            <button
              onClick={() => setShowWriteModal(true)}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-stone-900 text-white text-sm font-bold py-3.5 rounded-xl hover:bg-stone-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-edit-line" />
              리뷰 작성하기
            </button>
          </div>

          {/* Right: Review Cards Grid */}
          <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-sm font-bold text-stone-900">전체 후기</span>
                <span className="text-sm text-stone-400 ml-1.5">{reviews.length}개</span>
              </div>
              <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-100 rounded-full px-3 py-1.5">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-star-fill text-amber-400 text-xs" />
                </div>
                <span className="text-xs font-bold text-amber-700">평균 4.9점</span>
              </div>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {visibleReviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>

            {/* More Button */}
            {hasMore ? (
              <button
                onClick={() => setVisibleCount((c) => c + 4)}
                className="mt-2 w-full flex items-center justify-center gap-2 border border-stone-200 text-stone-600 text-sm font-semibold py-3 rounded-xl hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-down-line" />
                후기 더 보기
              </button>
            ) : (
              <div className="mt-2 text-center">
                <p className="text-xs text-stone-400 mb-3">모든 후기를 확인했어요</p>
                <button
                  onClick={() => setShowWriteModal(true)}
                  className="inline-flex items-center gap-1.5 border border-stone-200 text-stone-700 text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-edit-line" />
                  내 후기 남기기
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showWriteModal && <ReviewWriteModal onClose={() => setShowWriteModal(false)} />}
    </section>
  );
};

export default ReviewSection;
